import { useEffect, useState } from "react";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { ICertificateState } from "@/app_redux/reducers/slices/auth/certificate_slice";
import { ICustomerProfileState } from "@/app_redux/reducers/slices/auth/customer_profile_slice";
import { useDocumentListAPI } from "@/app/hooks/api_hooks/CertificateDocument/useDocumentListAPI";
import { ROUTES } from "@/app/routes";
import { useLoader } from "@/app/hooks/useLoader";
import NoItemFoundComponent from "@/app/components/ui/NoItemFoundComponent";
import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import TableComponent, { type TableColumn } from "@/app/components/ui/Reusable/TableComponent";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import { FiEdit } from "react-icons/fi";
import { AiOutlineEye } from "react-icons/ai";
import { MdDeleteOutline, MdErrorOutline } from "react-icons/md";
import { useDocumentDetailAPI } from "@/app/hooks/api_hooks/CertificateDocument/useDocumentDetailAPI";
import RBACFlag from "@/app/components/core/RBACFlag";
import { useLocation } from "react-router";
import { useDocumentDeleteAPI } from "@/app/hooks/api_hooks/CertificateDocument/useDocumentDeleteAPI";
import toast from "react-hot-toast";
import { Tooltip } from "react-tooltip";
import { ICertificatesDocumentListResponseBody } from "@/api_framework/api_modals/Certificates";

const ShowMoreText = ({ text, maxLength = 50 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  if (text?.length <= maxLength) {
    return <span>{text}</span>;
  }
  return (
    <div className="w-full md:w-[200px]">
      <p className="mb-1 text-[11px] whitespace-normal break-words">
        {isExpanded ? text : text.slice(0, maxLength) + "..."}
      </p>
      <button onClick={() => setIsExpanded(!isExpanded)} className="text-blue-600 underline text-[11px]">
        {isExpanded ? "Less" : "More"}
      </button>
    </div>
  );
};

const UploadedDocuments = () => {
  const { NO_CART } = useImageConstants();
  const { routeState } = useRouteInfo(
    ROUTES.ROOT.children.CUSTOMER_PROFILE.children.DETAILS_CUSTOMER.children.CERTIFICATES_TAB.id,
  );
  const { documentList } = routeState?.state as ICertificateState;
  const { routeState: customerRouteState } = useRouteInfo(ROUTES.ROOT.children.CUSTOMER_PROFILE.id);
  const { customerId } = customerRouteState?.state as ICustomerProfileState;
  const { execute: fetchDocumentList } = useDocumentListAPI();
  const isVaccinationLoader = useLoader("isvaccinationTableLoading");
  const { setVisible: openEditModal, setModalData: setEditModalData } = useModalActions(
    "CERTIFICATE_DOCUMENT_EDIT_MODAL",
  );
  const { setVisible: deleteDocument, setModalData: setCertificateDeleteModal } = useModalActions(
    "CERTIFICATE_DOCUMENT_DELETE_MODAL",
  );
  const { setVisible: openDeleteReasonModal, setModalData: setDeleteReasonModalData } = useModalActions(
    "CART_REMOVE_ITEM",
  );
  const { setVisible: viewDocument, setModalData: setCertificateViewModal } = useModalActions(
    "CERTIFICATE_DOCUMENT_VIEW_MODAL",
  );
  const { execute: fetchDocumentDetail } = useDocumentDetailAPI();
  const { execute: deleteDocumentAPI } = useDocumentDeleteAPI();
  const sortedDocuments = [...(documentList?.documents_list || [])].sort((a, b) => b?.created_at - a?.created_at);
  const location = useLocation();

  useEffect(() => {
    fetchDocumentList(customerId);
  }, []);

  const handleCertificateDocumentEditClick = async (itemIndex) => {
    try {
      const document = sortedDocuments?.[itemIndex];
      const documentId = document?.document_id;
      if (!documentId) return;
      await fetchDocumentDetail(customerId, documentId);
      setEditModalData({
        documentId: documentId,
        documents: document,
        returnPath: `${location.pathname}${location.search}`,
      });
      openEditModal();
    } catch (error) {
      console.error("Failed to fetch document detail", error);
    }
  };

  const handleDeleteDocumentWithReason = async (documentId: string, reason: string) => {
    const response = await deleteDocumentAPI({
      document_id: documentId,
      delete_reason: reason,
    });
    if (response?.status) {
      toast.success("Document deleted successfully.");
      fetchDocumentList(customerId);
    }
  };

  const handleCertificateDocumentDeleteClick = (item) => {
    const documents = sortedDocuments?.[item];
    const isJobCard = documents?.document_type?.toLowerCase() === "job card";

    if (isJobCard && documents?.document_id) {
      setDeleteReasonModalData({
        title: "Delete Job Card",
        primaryButtonText: "Delete",
        secondaryButtonText: "Cancel",
        itemName: documents?.document_type,
        reasonPlaceholder: "Enter reason for deleting this job card",
        description: (
          <>
            You are deleting <strong>{documents?.document_type}</strong>. Please enter a reason to continue.
          </>
        ),
        onConfirmDelete: (reason: string) => handleDeleteDocumentWithReason(documents.document_id, reason),
      });
      openDeleteReasonModal();
      return;
    }

    setCertificateDeleteModal({
      documents,
    });
    deleteDocument();
  };

  const handleCertificateDocumentViewClick = (item) => {
    const documents = sortedDocuments?.[item];
    setCertificateViewModal({
      documents,
    });
    viewDocument();
  };

  const dateFormat = (timestamp: number): string => {
    const date = new Date(timestamp * 1000);
    const day = date.getDate();
    const monthShort = date.toLocaleString("default", { month: "short" });
    const year = String(date.getFullYear()).slice(-2);
    return `${day} ${monthShort} ${year}`;
  };

  const hasValue = (value: unknown): boolean => {
    if (value === null || value === undefined) return false;
    if (typeof value === "string") return value.trim().length > 0;
    return true;
  };

  const getErrorMessage = (errorValue: unknown): string => {
    if (typeof errorValue === "string") return errorValue;
    if (errorValue === null || errorValue === undefined) return "";
    try {
      return JSON.stringify(errorValue);
    } catch {
      return "Document upload failed";
    }
  };

  const hasError = (documents: ICertificatesDocumentListResponseBody) =>
    hasValue(documents?.status) && hasValue(documents?.error);

  const columns: TableColumn<ICertificatesDocumentListResponseBody>[] = [
    {
      id: "created_at",
      header: "Uploaded Date",
      minWidth: 100,
      cell: ({ row }) => (row?.created_at ? dateFormat(row?.created_at) : "NA"),
    },
    {
      id: "document_type",
      header: "Document Type",
      minWidth: 120,
      className: "font-semibold",
      cell: ({ row }) => row?.document_type,
    },
    {
      id: "pet_name",
      header: "Pet",
      minWidth: 90,
      cell: ({ row }) => row?.pet_name,
    },
    {
      id: "appointment_details",
      header: "Appointment Details",
      minWidth: 160,
      cell: ({ row }) =>
        row?.appointment_details && Object.keys(row?.appointment_details)?.length > 0 ? (
          <>
            <p>{row?.appointment_details?.sub_service_name}</p>
            <p>{row?.appointment_details?.clinic_name}</p>
            <p>
              {row?.appointment_details?.booking_start_date} {row?.appointment_details?.booking_start_time}
            </p>
          </>
        ) : (
          "NA"
        ),
    },
    {
      id: "other_details",
      header: "Other Details",
      minWidth: 170,
      className: "text-gray-600",
      cell: ({ row }) => (
        <>
          <p>{row?.created_by}</p>
          <p>
            <span className="underline">Remarks:</span> <ShowMoreText text={row?.remarks} />
          </p>
        </>
      ),
    },
    {
      id: "action",
      header: "Action",
      align: "center",
      minWidth: 110,
      cell: ({ row, rowIndex }) => (
        <div className="flex gap-5 justify-center">
          {hasError(row) && (
            <div
              className="cursor-pointer text-red-500"
              data-tooltip-id="uploaded-document-error-tooltip"
              data-tooltip-content={getErrorMessage(row?.error)}
            >
              <MdErrorOutline className="w-4 h-4" />
            </div>
          )}

          {/* View */}
          <div className="cursor-pointer text-blue-600" onClick={() => handleCertificateDocumentViewClick(rowIndex)}>
            <AiOutlineEye className="w-4 h-4" />
          </div>

          {/* Edit */}
          <RBACFlag actionId="EDIT_DOCUMENT_ACTION_CTA">
            <div className="cursor-pointer" onClick={() => handleCertificateDocumentEditClick(rowIndex)}>
              <FiEdit className="w-4 h-4" />
            </div>
          </RBACFlag>

          {/* Delete */}
          <RBACFlag actionId="DELETE_DOCUMENT_ACTION_CTA">
            <div className="cursor-pointer" onClick={() => handleCertificateDocumentDeleteClick(rowIndex)}>
              <MdDeleteOutline className="w-4 h-4" color="red" />
            </div>
          </RBACFlag>
        </div>
      ),
    },
  ];

  return (
    <div className="mt-5">
      {isVaccinationLoader ? (
        <TableSkeletonComponent columnLength={5} bodyLength={6} />
      ) : (
        <>
          {Array.isArray(documentList?.documents_list) && documentList?.documents_list?.length > 0 ? (
            <>
              <TableComponent
                columns={columns}
                data={sortedDocuments}
                getRowId={(row, rowIndex) => row?.document_id ?? rowIndex}
                wrapCells
                containerClassName="!mr-0"
                scrollContainerClassName="max-h-[550px] overflow-y-auto overflow-x-auto"
                minTableWidthClassName="min-w-full"
                headerRowClassName="[&>th]:text-xs [&>th]:h-auto"
                rowClassName={(row) => (hasError(row) ? "!bg-[#FFF5F5]" : undefined)}
              />
              <Tooltip
                id="uploaded-document-error-tooltip"
                positionStrategy="fixed"
                style={{ fontSize: "10px", maxWidth: "260px", zIndex: 9999 }}
              />
            </>
          ) : (
            <div className="relative flex min-h-[280px] items-center justify-center sm:min-h-[360px]">
              <NoItemFoundComponent
                message="No Document Found"
                description=""
                imageURL={NO_CART}
                className="max-w-[180px] sm:max-w-[240px]"
                InputClassName="!my-0"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UploadedDocuments;
